import React from 'react';
import { CheckIcon, AlertCircleIcon, StarIcon, ArrowRightIcon } from 'lucide-react';
type FieldComparisonProps = {
  fieldName: string;
  fieldLabel: string;
  leftValue: string;
  rightValue: string;
  selectedSide: 'left' | 'right' | null;
  suggestedSide?: 'left' | 'right';
  onSelect: (side: 'left' | 'right') => void;
  formatValue?: (value: string) => string;
};
export function FieldComparison({
  fieldName,
  fieldLabel,
  leftValue,
  rightValue,
  selectedSide,
  suggestedSide,
  onSelect,
  formatValue = v => v
}: FieldComparisonProps) {
  const isMatch = leftValue === rightValue;
  const isConflict = !isMatch && leftValue && rightValue;
  const getValueClasses = (side: 'left' | 'right') => {
    const isSelected = selectedSide === side;
    const isSuggested = suggestedSide === side && !selectedSide;
    if (isSelected) {
      return 'border-blue-500 bg-blue-50 ring-2 ring-blue-200';
    }
    if (isMatch) {
      return 'border-green-300 bg-green-50';
    }
    if (isConflict) {
      return isSuggested ? 'border-yellow-400 bg-yellow-50 ring-1 ring-yellow-200' : 'border-yellow-300 bg-yellow-50/50';
    }
    return 'border-gray-200 bg-gray-50';
  };
  return <div className="group py-4 border-b border-gray-100 last:border-0" role="group" aria-label={`Compare ${fieldLabel}`} data-id="element-87">
      {/* Field Label */}
      <div className="flex items-center gap-2 mb-3" data-id="element-88">
        {isMatch ? <CheckIcon className="w-4 h-4 text-green-600" aria-hidden="true" data-id="element-89" /> : <AlertCircleIcon className="w-4 h-4 text-yellow-600" aria-hidden="true" data-id="element-90" />}
        <span className="text-sm font-semibold text-gray-900" data-id="element-91">
          {fieldLabel}
        </span>
        {isMatch && <span className="text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full font-medium" data-id="element-92">
            Match
          </span>}
        {isConflict && <span className="text-xs text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded-full font-medium" data-id="element-93">
            Conflict
          </span>}
      </div>

      {/* Value Comparison */}
      <div className="flex items-center gap-3" data-id="element-94">
        {/* Left Value */}
        <button onClick={() => onSelect('left')} disabled={isMatch} className={`flex-1 p-3 rounded-lg border-2 transition-all duration-200 text-left relative ${getValueClasses('left')} ${isMatch ? 'cursor-default' : 'cursor-pointer hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'}`} aria-pressed={selectedSide === 'left'} aria-label={`Select left value: ${formatValue(leftValue)}`} data-id="element-95">
          {suggestedSide === 'left' && !selectedSide && <div className="absolute -top-2 -right-2 bg-blue-600 rounded-full p-1 shadow-sm" title="AI Recommended" data-id="element-96">
              <StarIcon className="w-3 h-3 text-white fill-white" aria-hidden="true" data-id="element-97" />
            </div>}
          <span className="text-sm text-gray-900 break-words font-medium" data-id="element-98">
            {formatValue(leftValue) || <span className="text-gray-400 italic font-normal" data-id="element-99">Empty</span>}
          </span>
          {selectedSide === 'left' && <CheckIcon className="absolute bottom-2 right-2 w-4 h-4 text-blue-600" aria-hidden="true" data-id="element-100" />}
        </button>

        {/* Arrow */}
        <div className="flex-shrink-0" data-id="element-101">
          <ArrowRightIcon className="w-4 h-4 text-gray-400" aria-hidden="true" data-id="element-102" />
        </div>

        {/* Right Value */}
        <button onClick={() => onSelect('right')} disabled={isMatch} className={`flex-1 p-3 rounded-lg border-2 transition-all duration-200 text-left relative ${getValueClasses('right')} ${isMatch ? 'cursor-default' : 'cursor-pointer hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'}`} aria-pressed={selectedSide === 'right'} aria-label={`Select right value: ${formatValue(rightValue)}`} data-id="element-103">
          {suggestedSide === 'right' && !selectedSide && <div className="absolute -top-2 -right-2 bg-blue-600 rounded-full p-1 shadow-sm" title="AI Recommended" data-id="element-104">
              <StarIcon className="w-3 h-3 text-white fill-white" aria-hidden="true" data-id="element-105" />
            </div>}
          <span className="text-sm text-gray-900 break-words font-medium" data-id="element-106">
            {formatValue(rightValue) || <span className="text-gray-400 italic font-normal" data-id="element-107">Empty</span>}
          </span>
          {selectedSide === 'right' && <CheckIcon className="absolute bottom-2 right-2 w-4 h-4 text-blue-600" aria-hidden="true" data-id="element-108" />}
        </button>
      </div>
    </div>;
}